import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Popover from '@radix-ui/react-popover'
import { Home, LogOut, MessageSquare, Phone, User, X } from 'lucide-react'
// Moon, Sun — re-add when the theme toggle below is restored.
import { InputType } from '@/constants'
import { useSessionStore } from '@/stores'
import { useCartStore } from '@/stores/useCartStore'
// import { useTheme } from '@/hooks/useTheme' // theme toggle temporarily disabled
import Chatbot from './Chatbot'
import { currentSessionHome, FUNNEL_HOME, sessionHomePath } from '@/router/portalRegistry'

interface FloatingButtonsProps {
  isFAQ?: boolean
}

const FloatingButtons = ({ isFAQ = false }: FloatingButtonsProps) => {
  // const { theme, toggleTheme } = useTheme() // theme toggle temporarily disabled
  const navigate = useNavigate()
  const location = useLocation()
  const session = useSessionStore(s => s.session)
  const isMainMenuFn = useSessionStore(s => s.isMainMenu)
  const isMainMenu = isMainMenuFn()
  const isActive = useSessionStore(s => s.isSessionActiveAndReady)()
  const isSessionUserKnown = useSessionStore(s => s.isSessionUserKnown)()
  const campaign = useSessionStore(s => s.campaign)
  const goHome = useSessionStore(s => s.goHome)
  const logout = useSessionStore(s => s.logout)
  const hasCartItems = useCartStore(s => s.cart)?.length > 0

  const [enableChatbot, setEnableChatbot] = useState(false)
  const [userPopoverOpen, setUserPopoverOpen] = useState(false)

  const s = session as any
  const isFAQs = Number(session?.step_type) === InputType.Faqs
  const homeRoute = sessionHomePath(session, campaign)
  const isAtHome = location.pathname === homeRoute || (homeRoute === FUNNEL_HOME && isMainMenu)
  const showContactSupportButton = !!(s && isMainMenu && s.show_phone_link && s.phone_contact_url)
  const showChatbot = !!(s?.show_chatbot && (isMainMenu || isFAQ || isFAQs))
  const showHome = isActive && !isAtHome
  const showUserPopover = isSessionUserKnown && hasCartItems

  const hasChatbotMenu = showContactSupportButton || showChatbot

  const supportPhone = (() => {
    if (showContactSupportButton && s?.phone_contact_url) {
      const pos = s.phone_contact_url[4] === '+' ? 5 : 4
      return s.phone_contact_url.slice(pos)
    }
    return ''
  })()

  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement)
    const themeColor = rootStyles.getPropertyValue('--theme-color').trim()
    if (themeColor.startsWith('#') && (themeColor.length === 7 || themeColor.length === 4)) {
      const hex = themeColor.replace('#', '')
      const expanded =
        hex.length === 3
          ? hex
              .split('')
              .map(c => c + c)
              .join('')
          : hex
      const r = parseInt(expanded.substring(0, 2), 16)
      const g = parseInt(expanded.substring(2, 4), 16)
      const b = parseInt(expanded.substring(4, 6), 16)
      document.documentElement.style.setProperty('--box-shadow-color', `rgba(${r}, ${g}, ${b}, 0.14)`)
    }
  }, [session])

  const handleLogout = async () => {
    setUserPopoverOpen(false)
    try {
      await logout()
    } catch {
      // Navigate away regardless — a failed logout must not trap the user.
    }
    navigate('/')
  }

  const utilityBtnClass =
    'w-10 h-10 rounded-full flex items-center justify-center shadow-lg border transition-all duration-200 hover:scale-110 active:scale-95'

  return (
    <>
      <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2">
        {showHome && (
          <button
            type="button"
            onClick={async () => {
              try {
                await goHome()
                navigate(currentSessionHome())
              } catch {
                // Home is best-effort; a failed hop leaves the user where they are.
              }
            }}
            aria-label={homeRoute === FUNNEL_HOME ? 'Main Menu' : 'Products'}
            className={utilityBtnClass}
            style={{
              backgroundColor: 'var(--theme-color)',
              borderColor: 'var(--theme-color)',
              color: '#fff',
            }}>
            <Home size={18} />
          </button>
        )}

        {showUserPopover && (
          <Popover.Root open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                aria-label="Account"
                className={`${utilityBtnClass} sm:hidden`}
                style={{
                  backgroundColor: userPopoverOpen ? 'var(--theme-color)' : 'var(--content-background-color)',
                  borderColor: userPopoverOpen ? 'var(--theme-color)' : 'var(--border-color, #e5e7eb)',
                  color: userPopoverOpen ? '#fff' : 'var(--text-color)',
                }}>
                <User size={18} />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="user-floater-popover rounded-xl overflow-hidden shadow-elevation-3 animate-fade-up z-50"
                side="top"
                sideOffset={12}
                align="center"
                style={{
                  backgroundColor: 'var(--content-background-color)',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  minWidth: '160px',
                }}>
                <div
                  className="px-4 py-2.5 text-sm font-medium border-b"
                  style={{
                    color: 'var(--light-text-color)',
                    borderColor: 'var(--border-color, #e5e7eb)',
                    backgroundColor: 'color-mix(in srgb, var(--theme-color) 4%, var(--content-background-color))',
                  }}>
                  {(session?.customer as any)?.first_name} {(session?.customer as any)?.last_name}
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="no-transform w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors hover:bg-black/5"
                  style={{ color: 'var(--text-color)', background: 'none', border: 'none' }}>
                  <LogOut size={15} />
                  Logout
                </button>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}

        {/* Theme toggle temporarily disabled — light mode only for now. Restore
            this button (and the useTheme import/hook above) to re-enable. */}
        {/* <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className={utilityBtnClass}
          style={{
            backgroundColor: 'var(--content-background-color)',
            borderColor: 'var(--border-color, #e5e7eb)',
            color: 'var(--text-color)',
          }}>
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button> */}
      </div>

      {hasChatbotMenu && (
        <div className="floating-menu">
          {showContactSupportButton && s?.phone_contact_url && (
            <a
              href={s.phone_contact_url}
              className={`menu-btn contact-support ${showChatbot ? 'cb-enabled' : ''}`}
              onClick={e => e.stopPropagation()}>
              <Phone size={18} className="icon" />
              <span>Contact Support: </span>
              <strong>{supportPhone}</strong>
            </a>
          )}

          {showChatbot && (
            <div className="chatbot-trigger">
              {enableChatbot && <Chatbot onClose={() => setEnableChatbot(false)} />}
              <div
                className="menu-btn chatbot"
                onClick={e => {
                  e.stopPropagation()
                  setEnableChatbot(prev => !prev)
                }}>
                {enableChatbot ? (
                  <X size={22} className="text-white" />
                ) : (
                  <MessageSquare size={22} className="text-white" />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default FloatingButtons
